import { useEffect, useMemo, useState } from "react";
import {
    ArrowLeft,
    ArrowDown,
    ArrowUp,
    Bot,
    Check,
    GitBranch,
    Link2,
    Loader2,
    Save,
    Trash2,
    User,
} from "lucide-react";
import { toast } from "react-toastify";

import { getErrorMessage } from "../../Workspace/services/workspaceService";
import workflowService from "../services/workflowService";

const NODE_TYPES = {
    manual: { label: "Manual task", icon: User },
    automation: { label: "Automation", icon: Bot },
    decision: { label: "Decision", icon: GitBranch },
};

export default function WorkflowProperties({ workflow }) {
    const {
        workflow: workflowData,
        selectedStage,
        selectedNode,
        selectNode,
    } = workflow;
    const [saving, setSaving] = useState(false);
    const [confirmStageDelete, setConfirmStageDelete] = useState(false);
    const [disconnectingId, setDisconnectingId] = useState(null);
    const [stageForm, setStageForm] = useState({
        name: "",
        description: "",
        probability: 0,
        is_closed: false,
        is_active: true,
    });
    const [nodeForm, setNodeForm] = useState({
        name: "",
        description: "",
        node_type: "manual",
        display_order: 1,
        can_skip: false,
    });
    const allNodes = useMemo(
        () => (workflowData?.stages || []).flatMap((stage) =>
            (stage.nodes || []).map((node) => ({ ...node, stage_name: stage.name }))
        ),
        [workflowData]
    );
    const allTransitions = useMemo(
        () => (workflowData?.stages || []).flatMap((stage) => stage.transitions || []),
        [workflowData]
    );
    const connections = useMemo(() => {
        if (!selectedNode) return [];
        return allTransitions
            .filter((transition) =>
                String(transition.from_node_id) === String(selectedNode.id) ||
                String(transition.to_node_id) === String(selectedNode.id)
            )
            .map((transition) => {
                const outgoing = String(transition.from_node_id) === String(selectedNode.id);
                const peerId = outgoing ? transition.to_node_id : transition.from_node_id;
                return {
                    ...transition,
                    outgoing,
                    peer: allNodes.find((node) => String(node.id) === String(peerId)),
                };
            });
    }, [allNodes, allTransitions, selectedNode]);

    useEffect(() => {
        if (!selectedStage) return;
        setStageForm({
            name: selectedStage.name || "",
            description: selectedStage.description || "",
            probability: selectedStage.probability ?? 0,
            is_closed: Boolean(selectedStage.is_closed),
            is_active: selectedStage.is_active !== false,
        });
        setConfirmStageDelete(false);
    }, [selectedStage]);

    useEffect(() => {
        if (!selectedNode) return;
        setNodeForm({
            name: selectedNode.name || "",
            description: selectedNode.description || "",
            node_type: selectedNode.node_type || "manual",
            display_order: selectedNode.display_order || 1,
            can_skip: Boolean(selectedNode.can_skip),
        });
        setDisconnectingId(null);
    }, [selectedNode]);

    if (!selectedStage) {
        return (
            <div className="flex h-full items-center justify-center p-7 text-center">
                <div><GitBranch className="mx-auto h-7 w-7 text-[#a8b9c9]" /><p className="mt-3 text-sm font-bold text-[#294d63]">Select a stage</p><p className="mt-1 text-xs leading-5 text-[#7b8da9]">Its settings and actions will appear here.</p></div>
            </div>
        );
    }

    const saveStage = async (event) => {
        event.preventDefault();
        if (!stageForm.name.trim()) return;
        setSaving(true);
        try {
            await workflowService.updateStage(selectedStage.id, {
                name: stageForm.name.trim(),
                description: stageForm.description.trim() || null,
                probability: Number(stageForm.probability) || 0,
                is_closed: stageForm.is_closed,
                is_active: stageForm.is_active,
            });
            toast.success("Stage updated");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to update the stage."));
        } finally {
            setSaving(false);
        }
    };

    const deleteStage = async () => {
        setSaving(true);
        try {
            await workflowService.deleteStage(selectedStage.id);
            toast.success("Stage deleted");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to delete the stage."));
        } finally {
            setSaving(false);
            setConfirmStageDelete(false);
        }
    };

    const saveNode = async (event) => {
        event.preventDefault();
        if (!nodeForm.name.trim()) return;
        setSaving(true);
        try {
            await workflowService.updateNode(selectedNode.id, {
                name: nodeForm.name.trim(),
                description: nodeForm.description.trim() || null,
                node_type: nodeForm.node_type,
                display_order: Number(nodeForm.display_order) || 1,
                can_skip: nodeForm.can_skip,
                metadata: selectedNode.metadata || {},
            });
            toast.success("Action updated");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to update the action."));
        } finally {
            setSaving(false);
        }
    };

    const disconnect = async (transitionId) => {
        setSaving(true);
        try {
            await workflowService.deleteTransition(transitionId);
            setDisconnectingId(null);
            toast.success("Connection removed");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to remove the connection."));
        } finally {
            setSaving(false);
        }
    };

    const moveNode = async (direction) => {
        const ordered = [...(selectedStage.nodes || [])].sort(
            (first, second) => first.display_order - second.display_order
        );
        const currentIndex = ordered.findIndex((node) => String(node.id) === String(selectedNode.id));
        const targetIndex = currentIndex + direction;
        if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length) return;

        [ordered[currentIndex], ordered[targetIndex]] = [ordered[targetIndex], ordered[currentIndex]];
        setSaving(true);
        try {
            await workflowService.reorderNodes(
                ordered.map((node, index) => ({ id: node.id, display_order: index + 1 }))
            );
            toast.success("Action order updated");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to reorder actions."));
        } finally {
            setSaving(false);
        }
    };

    if (selectedNode) {
        const type = NODE_TYPES[nodeForm.node_type] || NODE_TYPES.manual;
        const TypeIcon = type.icon;

        return (
            <div className="flex h-full min-h-0 flex-col">
                <header className="border-b border-[#e4edf5] px-5 py-4">
                    <button type="button" onClick={() => selectNode(null)} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#147db5] hover:text-[#0f5b8a]"><ArrowLeft className="h-3.5 w-3.5" /> Stage settings</button>
                    <div className="mt-3 flex items-center justify-between gap-3">
                        <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a9ab2]">Action inspector</p><h2 className="mt-0.5 truncate text-base font-bold text-[#173552]">{selectedNode.name}</h2></div>
                        <div className="flex shrink-0 items-center gap-1 rounded-xl border border-[#dce8f3] bg-[#f7fbff] p-1">
                            <button type="button" disabled={saving || selectedNode.display_order <= 1} onClick={() => moveNode(-1)} title="Move action earlier" aria-label="Move action earlier" className="rounded-lg p-1.5 text-[#607b9f] hover:bg-white hover:text-[#147db5] disabled:cursor-not-allowed disabled:opacity-35"><ArrowUp className="h-3.5 w-3.5" /></button>
                            <button type="button" disabled={saving || selectedNode.display_order >= (selectedStage.nodes?.length || 0)} onClick={() => moveNode(1)} title="Move action later" aria-label="Move action later" className="rounded-lg p-1.5 text-[#607b9f] hover:bg-white hover:text-[#147db5] disabled:cursor-not-allowed disabled:opacity-35"><ArrowDown className="h-3.5 w-3.5" /></button>
                        </div>
                    </div>
                </header>

                <div className="min-h-0 flex-1 overflow-y-auto">
                    <form onSubmit={saveNode} className="space-y-4 p-5">
                        <Field label="Action name"><input value={nodeForm.name} onChange={(event) => setNodeForm((current) => ({ ...current, name: event.target.value }))} maxLength={150} required className={inputClass} /></Field>
                        <Field label="Instruction"><textarea value={nodeForm.description} onChange={(event) => setNodeForm((current) => ({ ...current, description: event.target.value }))} rows={3} placeholder="What must be completed?" className={`${inputClass} h-auto resize-none py-2.5 leading-5`} /></Field>
                        <Field label="Action type">
                            <div className="relative">
                                <TypeIcon className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#147db5]" />
                                <select value={nodeForm.node_type} onChange={(event) => setNodeForm((current) => ({ ...current, node_type: event.target.value }))} className={`${inputClass} pl-9`}>
                                    {Object.entries(NODE_TYPES).map(([value, config]) => <option key={value} value={value}>{config.label}</option>)}
                                </select>
                            </div>
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Order"><input type="number" min="1" value={nodeForm.display_order} onChange={(event) => setNodeForm((current) => ({ ...current, display_order: event.target.value }))} className={inputClass} /></Field>
                            <label className="mt-[22px] flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-[#d5e4f2] px-3 text-xs font-bold text-[#536b91]"><input type="checkbox" checked={nodeForm.can_skip} onChange={(event) => setNodeForm((current) => ({ ...current, can_skip: event.target.checked }))} className="h-4 w-4 rounded border-[#b9cad9] text-[#147db5]" /> Skippable</label>
                        </div>
                        <button type="submit" disabled={saving || !nodeForm.name.trim()} className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#147db5] text-sm font-bold text-white transition hover:bg-[#0f6f9f] disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save action</button>
                    </form>

                    <section className="border-t border-[#e4edf5] p-5">
                        <div className="flex items-center justify-between"><div><p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#8a9ab2]">Connections</p><h3 className="mt-0.5 text-sm font-bold text-[#173552]">Workflow paths</h3></div><span className="rounded-full bg-[#eff6fb] px-2 py-1 text-[10px] font-bold text-[#607b9f]">{connections.length}</span></div>
                        {connections.length === 0 ? (
                            <div className="mt-3 rounded-2xl border border-dashed border-[#cdddea] p-4 text-center"><Link2 className="mx-auto h-5 w-5 text-[#a8b9c9]" /><p className="mt-2 text-xs font-semibold text-[#7185a3]">No connections yet</p><p className="mt-1 text-[10px] leading-4 text-[#94a4b8]">Use the action menu to add or connect the next step.</p></div>
                        ) : (
                            <div className="mt-3 space-y-2">
                                {connections.map((connection) => (
                                    <div key={connection.id} className="rounded-xl border border-[#e1eaf3] bg-[#fbfdff] p-3">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0"><p className="text-[9px] font-bold uppercase tracking-wide text-[#8a9ab2]">{connection.outgoing ? "Goes to" : "Comes from"}</p><p className="mt-0.5 truncate text-xs font-bold text-[#294d63]">{connection.peer?.name || "Unknown action"}</p><p className="mt-0.5 truncate text-[10px] text-[#7b8da9]">{connection.action_group_name || "Continue"}{connection.peer?.stage_name ? ` · ${connection.peer.stage_name}` : ""}</p></div>
                                            {disconnectingId === connection.id ? (
                                                <div className="flex shrink-0 items-center gap-1"><button type="button" onClick={() => setDisconnectingId(null)} className="rounded-md px-1.5 py-1 text-[9px] font-bold text-[#7185a3]">Cancel</button><button type="button" disabled={saving} onClick={() => disconnect(connection.id)} className="rounded-md bg-red-600 px-2 py-1 text-[9px] font-bold text-white">Remove</button></div>
                                            ) : (
                                                <button type="button" onClick={() => setDisconnectingId(connection.id)} aria-label={`Remove connection with ${connection.peer?.name || "action"}`} title="Remove connection" className="rounded-lg p-1.5 text-[#9aa9ba] transition hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            </div>
        );
    }

    const totalNodes = selectedStage.nodes?.length || 0;

    return (
        <div className="flex h-full min-h-0 flex-col">
            <header className="border-b border-[#e4edf5] px-5 py-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#1689c1]">Stage inspector</p>
                <h2 className="mt-0.5 truncate text-base font-bold text-[#173552]">{selectedStage.name}</h2>
                <p className="mt-1 text-xs text-[#7b8da9]">{totalNodes} actions · Stage {selectedStage.display_order}</p>
            </header>

            <form onSubmit={saveStage} className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
                <Field label="Stage name"><input value={stageForm.name} onChange={(event) => setStageForm((current) => ({ ...current, name: event.target.value }))} maxLength={100} required className={inputClass} /></Field>
                <Field label="Purpose"><textarea value={stageForm.description} onChange={(event) => setStageForm((current) => ({ ...current, description: event.target.value }))} rows={4} placeholder="What should this stage accomplish?" className={`${inputClass} h-auto resize-none py-2.5 leading-5`} /></Field>
                <Field label="Conversion probability">
                    <div className="relative"><input type="number" min="0" max="100" value={stageForm.probability} onChange={(event) => setStageForm((current) => ({ ...current, probability: event.target.value }))} className={`${inputClass} pr-9`} /><span className="absolute right-3 top-2.5 text-xs font-bold text-[#7b8da9]">%</span></div>
                </Field>

                <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-[#dce8f3] p-3.5">
                    <input type="checkbox" checked={stageForm.is_active} onChange={(event) => setStageForm((current) => ({ ...current, is_active: event.target.checked }))} className="mt-0.5 h-4 w-4 rounded border-[#b9cad9] text-[#147db5]" />
                    <span><span className="block text-xs font-bold text-[#294d63]">Active stage</span><span className="mt-0.5 block text-[10px] leading-4 text-[#7b8da9]">Available to leads moving through this pipeline.</span></span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-[#dce8f3] p-3.5">
                    <input type="checkbox" checked={stageForm.is_closed} onChange={(event) => setStageForm((current) => ({ ...current, is_closed: event.target.checked }))} className="mt-0.5 h-4 w-4 rounded border-[#b9cad9] text-[#147db5]" />
                    <span><span className="block text-xs font-bold text-[#294d63]">Closed outcome</span><span className="mt-0.5 block text-[10px] leading-4 text-[#7b8da9]">Use for terminal stages such as Won or Lost.</span></span>
                </label>

                <button type="submit" disabled={saving || !stageForm.name.trim()} className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#147db5] text-sm font-bold text-white transition hover:bg-[#0f6f9f] disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save stage</button>

                <div className="border-t border-[#edf2f7] pt-4">
                    {!confirmStageDelete ? (
                        <button type="button" disabled={saving || totalNodes > 0} onClick={() => setConfirmStageDelete(true)} title={totalNodes > 0 ? "Remove all actions before deleting this stage" : "Delete stage"} className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-red-100 bg-red-50 text-xs font-bold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-45"><Trash2 className="h-4 w-4" /> Delete stage</button>
                    ) : (
                        <div className="rounded-2xl border border-red-100 bg-red-50 p-3"><p className="text-xs font-bold text-red-700">Delete this empty stage?</p><div className="mt-2 flex justify-end gap-2"><button type="button" onClick={() => setConfirmStageDelete(false)} className="px-2 py-1.5 text-[10px] font-bold text-[#607b9f]">Cancel</button><button type="button" onClick={deleteStage} disabled={saving} className="rounded-lg bg-red-600 px-3 py-1.5 text-[10px] font-bold text-white">Delete</button></div></div>
                    )}
                    {totalNodes > 0 && <p className="mt-2 text-center text-[10px] leading-4 text-[#8a9ab2]">Remove the stage’s actions before deleting it.</p>}
                </div>
            </form>
        </div>
    );
}

const inputClass = "h-10 w-full rounded-xl border border-[#d5e4f2] bg-white px-3 text-sm text-[#173552] outline-none transition placeholder:text-[#9bacc0] focus:border-[#49a9d8] focus:ring-2 focus:ring-[#dff3fc]";

function Field({ label, children }) {
    return <label className="block"><span className="mb-1.5 block text-xs font-bold text-[#294d63]">{label}</span>{children}</label>;
}
